import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBenefitHistoryStore } from '../../services/benefitsService/benefit.store';
import { BenefitHistoryItem } from '../../services/benefitsService/benefit.dto';
import { useConsumeBenefitService } from '../../services/ConsumeBenefit/ConsumeBenefitService';
import type {
  CouponDetailsResponse,
  ConsumeCouponResponse,
  CouponDetails,
  CouponDetailsData,
} from '../../services/ConsumeBenefit/ConsumeBenefitInterface';

const useConsumeBenefitController = () => {
  const [couponData, setCouponData] = useState<CouponDetailsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<
    'redeemed' | 'stock_reached' | 'expired' | ''
  >('');
  const [renewalFrequency, setRenewalFrequency] = useState<string>('');
  const [canConsume, setCanConsume] = useState(false);
  const [isConsumed, setIsConsumed] = useState(false);
  const benefitDescriptionRef = useRef('');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getCouponById, consumeBenefit } = useConsumeBenefitService();

  useEffect(() => {
    loadCouponData();
  }, []);

  const loadCouponData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Obtener el couponId del parámetro 'q' de la URL
      const couponId = searchParams.get('q');

      if (!couponId) {
        throw new Error('No se proporcionó un ID de cupón válido en la URL');
      }

      const response = await getCouponById(couponId);

      if (!response) {
        throw new Error(
          'El cupón no existe o no se pudo obtener la información, inténtalo de nuevo más tarde'
        );
      }

      // Verificar si la respuesta es un error
      if ('error' in response) {
        throw new Error(
          response.message || 'Error al obtener los datos del beneficio'
        );
      }

      // Verificar si la respuesta tiene la estructura correcta
      if (!response.success || !response.data) {
        throw new Error('Respuesta del servidor inválida');
      }

      // valido si el cupon no está expirado con el endDate
      const currentDate = new Date();
      const endDate = new Date(response.data.benefit.endDate);

      if (currentDate > endDate) {
        setError('Oops, beneficio vencido 🫠');
        setCanConsume(false);
        setCodeError('expired');
      } else {
        setCanConsume(true);
      }

      setCouponData(response.data);
      const type =
        response.data.benefit.type === 'discount' ? 'Descuento' : 'Promoción';
      benefitDescriptionRef.current = `${type} - ${
        response.data.benefit.benefitTag || ''
      }`;
    } catch (error) {
      console.error('Error loading coupon data:', error);
      setError(
        error instanceof Error
          ? error.message
          : 'Error al cargar los datos del beneficio'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const goToHome = () => {
    navigate('/');
  };

  const handleConsumeBenefit = async () => {
    if (!couponData || !canConsume) return;

    try {
      setIsLoading(true);
      setError(null);

      // Llamar al servicio para consumir el beneficio
      const response = await consumeBenefit(couponData.coupon._id);

      if (!response) {
        throw new Error('No se pudo procesar el consumo del beneficio');
      }

      // Manejar diferentes tipos de respuesta
      if ('success' in response && response.success) {
        // Consumo exitoso
        const historyItem: BenefitHistoryItem = {
          status: 'successful',
          code: couponData.coupon._id,
          consumedDate: new Date(),
          expirationDate: new Date(couponData.benefit.endDate),
          startValidDate: new Date(couponData.benefit.startDate),
        };

        // Mostrar mensaje de éxito y deshabilitar el botón
        setCanConsume(false);
        setIsConsumed(true);

        // Opcional: redirigir después de unos segundos
        // setTimeout(() => {
        //   handleBack();
        // }, 3000);
      } else if (
        'success' in response &&
        'code' in response &&
        !response.success
      ) {
        setCodeError(response.code);
        setError(
          response.code === 'redeemed'
            ? 'Ya usaron este beneficio 🤭'
            : 'Oops, ya finalizó 🫠'
        );
        setRenewalFrequency(response.data?.renewalFrequency || '');
        setCanConsume(false);
      } else if ('error' in response) {
        // Error del servidor
        throw new Error(response.message || 'Error del servidor');
      }
    } catch (error) {
      console.error('Error consuming benefit:', error);
      setError(
        error instanceof Error
          ? error.message
          : 'Error al consumir el beneficio. Inténtalo de nuevo.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {
    couponData,
    isLoading,
    error,
    canConsume,
    isConsumed,
    codeError,
    renewalFrequency,
    handleBack,
    goToHome,
    handleConsumeBenefit,
    benefitDescription: benefitDescriptionRef.current,
  };
};

export default useConsumeBenefitController;
